import { Dispatch, SetStateAction } from "react";

import { customAssetLibraryImageOrder, type BannerSource } from "@simplyk/common";
import { useSnackbar } from "notistack";
import { FieldValues, Path, PathValue, type UseFormReturn } from "react-hook-form";

import { ImageSelectionResult } from "../../SelectImageDialog/types";
import { FundraiserContext, ImageAltTextMetadata, ImageUploadContext, mapImageSourceToBannerSource } from "../types";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { buildImageUploadDedupeKey, enqueueImageUpload } from "@/common/helpers/imageUploadQueue";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";

export interface UpdateFileUrlOptions {
  sourceMetadata: ImageSelectionResult | null;
}

interface UseImageUploadParams<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  isUploadingName: Path<T>;
  uploadPreset: "default" | "logo-preset" | "square_800";
  uploadContext: ImageUploadContext;
  fundraiserContext: FundraiserContext | undefined;
  fullAmplitudePayload: Record<string, unknown>;
  isCustomAssetLibraryActive: boolean;
  persistToAssetLibrary: boolean;
  imageSourceMetadata: ImageSelectionResult | null;
  setImageSourceMetadata: Dispatch<SetStateAction<ImageSelectionResult | null>>;
  setFileUrl: Dispatch<SetStateAction<string | undefined | null>>;
  fillImageMetadata: (url: string) => void;
  onImageSourceChange: ((source: BannerSource | null) => void) | undefined;
  onImageAltTextMetadataChange: ((metadata: ImageAltTextMetadata | null) => void) | undefined;
}

export const useImageUpload = <T extends FieldValues>({
  form,
  name,
  isUploadingName,
  uploadPreset,
  uploadContext,
  fundraiserContext,
  fullAmplitudePayload,
  isCustomAssetLibraryActive,
  persistToAssetLibrary,
  imageSourceMetadata,
  setImageSourceMetadata,
  setFileUrl,
  fillImageMetadata,
  onImageSourceChange,
  onImageAltTextMetadataChange,
}: UseImageUploadParams<T>) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { logAmplitudeEvent } = useAmplitude();

  const { setValue } = form;
  const trpcUtils = trpc.useUtils();
  const { mutateAsync: uploadImage } = trpc.uploadImage.useMutation();
  const { mutateAsync: uploadImageToCustomAssetLibrary } = trpc.uploadImageToCustomAssetLibrary.useMutation();
  const { mutateAsync: touchCustomAssetLibraryImage } = trpc.touchCustomAssetLibraryImage.useMutation();

  const emitAltText = (altText: string | null) => {
    if (isCustomAssetLibraryActive) {
      onImageAltTextMetadataChange?.({ altText, status: "ready" });
    }
  };

  // Slow-upload fallback: poll the alt text endpoint until the AI alt text lands or we give up.
  const pollForAltText = async (publicId: string): Promise<string | null> => {
    const MAX_ATTEMPTS = 15;
    const INTERVAL_MS = 3000;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, INTERVAL_MS));
      const altTextResult = await trpcUtils.getCustomAssetLibraryImageAltText
        .fetch({
          publicId,
          fundraiserToken: fundraiserContext?.fundraiserToken,
          donationFormId: fundraiserContext?.donationFormId,
        })
        // Transient failure — keep polling; we stop after MAX_ATTEMPTS.
        .catch(() => null);
      if (altTextResult?.ready) {
        return altTextResult.altText ?? null;
      }
    }
    return null;
  };

  const persistFreshUploadToLibrary = async (sourceUrl: string): Promise<{ publicId: string | null }> => {
    const { data } = await uploadImageToCustomAssetLibrary({
      sourceUrl,
      uploadPreset,
      uploadContext,
      fundraiserToken: fundraiserContext?.fundraiserToken,
      donationFormId: fundraiserContext?.donationFormId,
      ticketingId: fundraiserContext?.ticketingId,
    }).catch(() => ({ data: null }));

    const assetLibraryImage = data?.assetLibraryImage;
    if (assetLibraryImage) {
      trpcUtils.listCustomAssetLibraryImages.setData({ limit: 50 }, (prev) => {
        const withoutDuplicate = (prev ?? []).filter((i) => i.publicId !== assetLibraryImage.publicId);
        return [assetLibraryImage, ...withoutDuplicate].sort(customAssetLibraryImageOrder);
      });
    }
    return { publicId: assetLibraryImage?.publicId ?? null };
  };

  // Images in the "My images" library appear in order of last used
  const updateMyImageLastUsedAt = async (publicId: string): Promise<void> => {
    const { error } = await touchCustomAssetLibraryImage({ publicId });
    if (error) {
      return;
    }
    const now = new Date().toISOString();
    trpcUtils.listCustomAssetLibraryImages.setData({ limit: 50 }, (prev) => {
      if (!prev) {
        return prev;
      }
      return [...prev]
        .map((img) => (img.publicId === publicId ? { ...img, lastUsedAt: now } : img))
        .sort(customAssetLibraryImageOrder);
    });
  };

  const resolveFreshUploadAltText = async (uploadedUrl: string): Promise<void> => {
    const { publicId } = await persistFreshUploadToLibrary(uploadedUrl);

    // The image was replaced while the upload ran - drop the stale alt text.
    if (form.getValues(name) !== uploadedUrl) {
      return;
    }

    if (!publicId) {
      emitAltText(null);
      return;
    }

    // Captioning always runs asynchronously server-side; poll until the AI alt text lands, then emit it.
    const altText = await pollForAltText(publicId);
    // Bring the freshly-persisted image into "My images" now that alt text generation is done.
    void trpcUtils.listCustomAssetLibraryImages.invalidate();
    if (form.getValues(name) !== uploadedUrl) {
      return;
    }
    emitAltText(altText);
  };

  const updateFileUrl = async (
    image: string | undefined | null,
    options?: UpdateFileUrlOptions
  ): Promise<string | null> => {
    setValue(isUploadingName, true as PathValue<T, Path<T>>);
    setFileUrl(image);

    if (!image) {
      setValue(isUploadingName, false as PathValue<T, Path<T>>);
      return null;
    }

    setValue(name, image as PathValue<T, Path<T>>, { shouldDirty: true });

    if (isCustomAssetLibraryActive) {
      onImageAltTextMetadataChange?.({ altText: null, status: "pending" });
    }

    // Callers that upload in the same tick as their selection pass it, since the state is not re-rendered yet.
    const sourceMetadata = options ? options.sourceMetadata : imageSourceMetadata;
    const source = sourceMetadata?.source ?? "upload";
    // Zeffy library assets aren't cached per-org, so we generate alt text for their per-use copy. All other
    // sources supply alt text from their own metadata (my_images cache, unsplash API, fresh-upload library clone).
    const detection = source === "zeffy_library";

    const { data: uploadResult, error: uploadError } = await enqueueImageUpload({
      dedupeKey: buildImageUploadDedupeKey({ file: image, uploadPreset }),
      upload: () =>
        uploadImage({
          file: image,
          uploadPreset,
          fundraiserToken: fundraiserContext?.fundraiserToken,
          donationFormId: fundraiserContext?.donationFormId,
          detection,
        }),
    }).catch((networkError: unknown) => ({
      data: null,
      error: {
        httpStatus: undefined as number | undefined,
        code: "network_error",
        message: networkError instanceof Error ? networkError.message : "Unknown error",
      },
    }));

    const uploadedUrl = uploadResult?.url ?? null;

    if (uploadError || !uploadedUrl) {
      const errorShape = uploadError as { httpStatus?: number; code?: string; message?: string } | null;
      const isUnauthorized = errorShape?.httpStatus === 401;

      logAmplitudeEvent(AmplitudeEvents.ImageUploaderUploadFailed, {
        source,
        error_code: isUnauthorized ? "unauthorized" : (errorShape?.code ?? "unknown_error"),
        http_status: errorShape?.httpStatus,
        error_message: errorShape?.message ?? "Unknown error",
        ...fullAmplitudePayload,
      });

      if (isUnauthorized) {
        enqueueSnackbar(t("dashboard", "imageUploader.sessionExpired"), { vibe: "danger" });
      }

      setValue(isUploadingName, false as PathValue<T, Path<T>>);
      setFileUrl(null);
      setValue(name, null as PathValue<T, Path<T>>);
      if (isCustomAssetLibraryActive) {
        onImageAltTextMetadataChange?.(null);
      }
      return null;
    }

    setValue(name, uploadedUrl as PathValue<T, Path<T>>, { shouldDirty: true });
    fillImageMetadata(image);

    logAmplitudeEvent(AmplitudeEvents.ImageUploaderUploadSuccess, {
      source,
      ...(source === "zeffy_library" && {
        zeffy_image_style: sourceMetadata?.zeffyImageStyle,
        zeffy_image_name: sourceMetadata?.zeffyImageName,
      }),
      ...fullAmplitudePayload,
    });

    onImageSourceChange?.(mapImageSourceToBannerSource(source));

    // The image URL is committed; unblock the UI (render the image / enable the step) before running the
    // potentially slow asset-library persist + AI captioning, which continues in the background.
    setImageSourceMetadata(null);
    setValue(isUploadingName, false as PathValue<T, Path<T>>);

    // Resolve alt text. Known-alt sources emit synchronously. Only fresh uploads defer to the async alt text path.
    if (source === "my_images") {
      emitAltText(sourceMetadata?.altText ?? null);
      if (sourceMetadata?.publicId) {
        void updateMyImageLastUsedAt(sourceMetadata.publicId);
      }
    } else if (source === "unsplash") {
      emitAltText(sourceMetadata?.altText ?? null);
    } else if (source === "zeffy_library") {
      emitAltText(uploadResult?.altText ?? null);
    } else if (!persistToAssetLibrary) {
      emitAltText(null);
    } else {
      void resolveFreshUploadAltText(uploadedUrl);
    }

    return uploadedUrl;
  };

  return { updateFileUrl };
};
